import zod from "zod";

const clipboardMetadataSchema = zod
  .object({
    id: zod.string().optional(),
    url: zod.string().optional(),
    site: zod.string().optional(),
    jobId: zod.string().optional(),
    timestamp: zod.string().optional(),
    source: zod.string().optional(),
  })
  .optional();

const clipboardEntityRefSchema = zod.object({
  id: zod.string().optional(),
  name: zod.string().optional(),
});

// Tina-HITL artist/genre refs allow missing names — e.g. when only a matched
// Mood id is known. Consumers must handle missing `name` gracefully.
const clipboardNamedEntityRefSchema = zod.object({
  id: zod.string().optional(),
  name: zod.string().optional(),
});

const tinaHitlMetadataSchema = zod.object({
  tinaItemId: zod.string(),
  tinaRunId: zod.string(),
  hitlReason: zod.string(),
  contentType: zod.literal("music_event"),
  url: zod.string().optional(),
  site: zod.string().optional(),
});

export const legacyClipboardDataSchema = zod.object({
  source: zod.literal("clipboard"),
  name: zod.string(),
  about: zod.string(),
  startDate: zod.string(),
  endDate: zod.string().optional(),
  type: zod.string(),
  genres: zod.string(),
  venue: zod.string(),
  lineup: zod.string(),
  timezone: zod.string(),
  city: zod.string().optional(),
  ticketingUrl: zod.string().optional(),
  metadata: clipboardMetadataSchema,
});

export const tinaHitlClipboardDataSchema = zod.object({
  source: zod.literal("tina-hitl"),
  name: zod.string().optional(),
  about: zod.string().optional(),
  startDate: zod.string().optional(),
  endDate: zod.string().optional(),
  type: zod.string().optional(),
  genres: zod.array(clipboardNamedEntityRefSchema).optional(),
  venue: clipboardEntityRefSchema.optional(),
  lineup: zod.array(clipboardNamedEntityRefSchema).optional(),
  timezone: zod.string().optional(),
  city: zod.string().optional(),
  ticketingUrl: zod.string().optional(),
  matchedEntityIds: zod
    .object({
      venueId: zod.string().optional(),
      artistIds: zod.array(zod.string()).default([]),
    })
    .optional(),
  autoCloseHitl: zod.literal(true).optional(),
  metadata: tinaHitlMetadataSchema,
  warnings: zod.array(zod.string()).optional(),
});

// Legacy clipboard payloads predate the `source` discriminator — default it
// to "clipboard" so the discriminated union can route them correctly.
export const clipboardDataSchema = zod.preprocess(
  (value) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !("source" in value)
    ) {
      return { ...(value as Record<string, unknown>), source: "clipboard" };
    }
    return value;
  },
  zod.discriminatedUnion("source", [
    tinaHitlClipboardDataSchema,
    legacyClipboardDataSchema,
  ]),
);

export type LegacyClipboardData = zod.infer<typeof legacyClipboardDataSchema>;
export type TinaHitlClipboardData = zod.infer<
  typeof tinaHitlClipboardDataSchema
>;
export type ClipboardData = zod.infer<typeof clipboardDataSchema>;

export interface ClipboardEntityRef {
  id?: string;
  name?: string;
}

/**
 * Tina-HITL clipboard refs may carry only an `id` (e.g. when a matched Mood
 * entity is known but its display name is not on the payload). `name` is
 * therefore optional — consumers must handle the id-only case (look the
 * entity up via repo, or render the id).
 */
export interface ClipboardNamedEntityRef {
  id?: string;
  name?: string;
}

export interface TinaHitlSource {
  tinaItemId: string;
  tinaRunId: string;
  hitlReason: string;
  contentType: "music_event";
  autoCloseHitl: true;
}

export interface ClipboardEventMetadata {
  id?: string;
  url?: string;
  site?: string;
  jobId?: string;
  timestamp?: string;
  source?: string;
}

export function isTinaHitlClipboardData(
  data: ClipboardData,
): data is TinaHitlClipboardData {
  return data.source === "tina-hitl";
}

export function readClipboardVenueRef(
  data: ClipboardData,
): ClipboardEntityRef | undefined {
  if (isTinaHitlClipboardData(data)) {
    return normalizeEntityRef({
      id: data.venue?.id ?? data.matchedEntityIds?.venueId,
      name: data.venue?.name,
    });
  }
  return normalizeEntityRef({ name: data.venue });
}

export function readClipboardLineupRefs(
  data: ClipboardData,
): ClipboardNamedEntityRef[] {
  if (!isTinaHitlClipboardData(data)) {
    return splitLegacyNames(data.lineup).map((name) => ({ name }));
  }

  const refs: ClipboardNamedEntityRef[] = (data.lineup ?? []).map((artist) => ({
    id: artist.id,
    name: artist.name,
  }));
  const seenIds = new Set(
    refs.flatMap((artist) => (artist.id ? [artist.id] : [])),
  );

  for (const artistId of data.matchedEntityIds?.artistIds ?? []) {
    if (seenIds.has(artistId)) continue;
    seenIds.add(artistId);
    refs.push({ id: artistId });
  }

  return refs.filter((artist) => Boolean(artist.id || artist.name?.trim()));
}

export function readClipboardGenreRefs(
  data: ClipboardData,
): ClipboardNamedEntityRef[] {
  if (isTinaHitlClipboardData(data)) return data.genres ?? [];
  return splitLegacyNames(data.genres).map((name) => ({ name }));
}

export function readTinaHitlSource(
  data: ClipboardData,
): TinaHitlSource | undefined {
  if (!isTinaHitlClipboardData(data) || !data.autoCloseHitl) return undefined;
  return {
    tinaItemId: data.metadata.tinaItemId,
    tinaRunId: data.metadata.tinaRunId,
    hitlReason: data.metadata.hitlReason,
    contentType: data.metadata.contentType,
    autoCloseHitl: true,
  };
}

export function readEventMetadata(
  data: ClipboardData,
): ClipboardEventMetadata | undefined {
  if (isTinaHitlClipboardData(data)) {
    return {
      id: data.metadata.tinaItemId,
      url: data.metadata.url,
      site: data.metadata.site,
      jobId: data.metadata.tinaRunId,
      source: data.source,
    };
  }
  return data.metadata;
}

function normalizeEntityRef(
  ref: ClipboardEntityRef,
): ClipboardEntityRef | undefined {
  const id = ref.id?.trim();
  const name = ref.name?.trim();
  return id || name ? { id, name } : undefined;
}

function splitLegacyNames(value: string): string[] {
  return value
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}
